import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Grid,
    Paper,
    Card,
    CardContent,
    Tabs,
    Tab,
    CircularProgress,
    Alert, Button
} from '@mui/material';
import {
    LineChart,
    Line,
    BarChart,
    Bar,
    PieChart,
    Pie,
    Cell,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer
} from 'recharts';
import { BarChart2, TrendingUp, PieChart as PieChartIcon, Home } from 'react-feather';
import { statusColors } from './mockData';
import { ApiDetails, KEY } from "../../Constants";
import CryptoJS from "crypto-js";
import { handle401Unauthorized, handle403Forbidden } from '../../utils/authErrorHandler';


// Tab panel component
function TabPanel(props) {
    const { children, value, index, ...other } = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`analytics-tabpanel-${index}`}
            aria-labelledby={`analytics-tab-${index}`}
            {...other}
        >
            {value === index && (
                <Box sx={{ py: 3 }}>
                    {children}
                </Box>
            )}
        </div>
    );
}

const TicketAnalytics = ({ tickets, showAlert }) => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [tabValue, setTabValue] = useState(0);
    const [ticketTrend, setTicketTrend] = useState([]);
    const [resolutionTime, setResolutionTime] = useState([]);
    const [categoryDistribution, setCategoryDistribution] = useState([]);
    const [priorityDistribution, setPriorityDistribution] = useState([]);
    const [propertyComparison, setPropertyComparison] = useState([]);
const [originalText, setOriginalText] = useState(() => {
    const encryptedData = localStorage.getItem("myMap");
    return encryptedData 
        ? CryptoJS.AES.decrypt(encryptedData, KEY).toString(CryptoJS.enc.Utf8)
        : "";
});;

    // Get landlord_id
    const getLandlordId = () => {
        try {
            let myMapDetailsDecrypted = CryptoJS.AES.decrypt(
                localStorage.getItem("myMap"),
                KEY
            );
            let originalText = myMapDetailsDecrypted.toString(CryptoJS.enc.Utf8);
            return JSON.parse(originalText)[0][1];
        } catch (error) {
            console.error("Error getting landlord ID:", error);
            setError("Unable to get landlord ID. Please try again.");
            return null;
        }
    };

    // Process ticket data if provided directly
    const processTicketData = (ticketsData) => {
        if (!ticketsData || ticketsData.length === 0) return;

        try {
            setLoading(true);

            // Calculate ticket trend by month
            const ticketsByMonth = {};
            ticketsData.forEach(ticket => {
                const date = new Date(ticket.date_created || ticket.createdAt);
                const monthYear = `${date.toLocaleString('default', { month: 'short' })} ${date.getFullYear()}`;

                if (!ticketsByMonth[monthYear]) {
                    ticketsByMonth[monthYear] = {
                        name: monthYear,
                        tickets: 0,
                        resolved: 0
                    };
                }

                ticketsByMonth[monthYear].tickets += 1;

                if (ticket.status === 'RESOLVED' || ticket.status === 'CLOSED' ||
                    ticket.status === 'Resolved' || ticket.status === 'Closed') {
                    ticketsByMonth[monthYear].resolved += 1;
                }
            });

            const sortedMonths = Object.values(ticketsByMonth).sort((a, b) => {
                const [aMonth, aYear] = a.name.split(' ');
                const [bMonth, bYear] = b.name.split(' ');
                return new Date(`${aMonth} 1, ${aYear}`) - new Date(`${bMonth} 1, ${bYear}`);
            });

            setTicketTrend(sortedMonths);

            // Calculate average resolution time by month
            const resolutionByMonth = {};
            ticketsData.forEach(ticket => {
                if (!(['RESOLVED', 'CLOSED', 'Resolved', 'Closed'].includes(ticket.status))) return;

                const created = new Date(ticket.date_created || ticket.createdAt);
                const resolved = new Date(ticket.date_updated || ticket.updatedAt);

                if (isNaN(created) || isNaN(resolved)) return;

                const diffTime = Math.abs(resolved - created);
                const diffDays = diffTime / (1000 * 60 * 60 * 24);

                const monthYear = `${created.toLocaleString('default', { month: 'short' })} ${created.getFullYear()}`;

                if (!resolutionByMonth[monthYear]) {
                    resolutionByMonth[monthYear] = {
                        name: monthYear,
                        totalDays: 0,
                        count: 0
                    };
                }

                resolutionByMonth[monthYear].totalDays += diffDays;
                resolutionByMonth[monthYear].count += 1;
            });

            const avgResolutionTime = Object.entries(resolutionByMonth).map(([monthYear, data]) => ({
                name: monthYear,
                days: parseFloat((data.totalDays / data.count).toFixed(1))
            }));

            setResolutionTime(avgResolutionTime.sort((a, b) => {
                const [aMonth, aYear] = a.name.split(' ');
                const [bMonth, bYear] = b.name.split(' ');
                return new Date(`${aMonth} 1, ${aYear}`) - new Date(`${bMonth} 1, ${bYear}`);
            }));

            // Calculate category distribution
            const categories = {};
            ticketsData.forEach(ticket => {
                const category = ticket.problem_type_name || ticket.category || 'Uncategorized';

                if (!categories[category]) {
                    categories[category] = 0;
                }

                categories[category] += 1;
            });

            setCategoryDistribution(Object.entries(categories).map(([name, value]) => ({
                name,
                value
            })).sort((a, b) => b.value - a.value));

            // Calculate priority distribution
            const priorities = {
                High: 0,
                Medium: 0,
                Low: 0
            };

            ticketsData.forEach(ticket => {
                if (ticket.priority) {
                    priorities[ticket.priority] += 1;
                }
            });

            setPriorityDistribution(Object.entries(priorities).map(([name, value]) => ({
                name,
                value
            })));

            // Calculate property comparison
            const properties = {};
            ticketsData.forEach(ticket => {
                const propertyName = ticket.property_name || ticket.property?.name || 'Unknown';

                if (!properties[propertyName]) {
                    properties[propertyName] = {
                        property: propertyName,
                        tickets: 0,
                        open: 0,
                        resolved: 0,
                        totalResolutionDays: 0,
                        resolvedCount: 0
                    };
                }

                properties[propertyName].tickets += 1;

                if (ticket.status === 'OPEN' || ticket.status === 'IN PROGRESS' ||
                    ticket.status === 'Open' || ticket.status === 'In Progress') {
                    properties[propertyName].open += 1;
                } else if (ticket.status === 'RESOLVED' || ticket.status === 'CLOSED' ||
                    ticket.status === 'Resolved' || ticket.status === 'Closed') {
                    properties[propertyName].resolved += 1;

                    const created = new Date(ticket.date_created || ticket.createdAt);
                    const resolved = new Date(ticket.date_updated || ticket.updatedAt);

                    if (!isNaN(created) && !isNaN(resolved)) {
                        const diffTime = Math.abs(resolved - created);
                        const diffDays = diffTime / (1000 * 60 * 60 * 24);

                        properties[propertyName].totalResolutionDays += diffDays;
                        properties[propertyName].resolvedCount += 1;
                    }
                }
            });

            const propertyStats = Object.values(properties).map(prop => ({
                ...prop,
                resolutionTime: prop.resolvedCount > 0 ?
                    parseFloat((prop.totalResolutionDays / prop.resolvedCount).toFixed(1)) : 0
            })).sort((a, b) => b.tickets - a.tickets);

            setPropertyComparison(propertyStats);
            setError(null);
        } catch (error) {
            console.error("Error processing ticket data:", error);
            setError("Error processing ticket data. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    // Fetch analytics data from API
    const fetchAnalyticsData = async () => {
        try {
            setLoading(true);
            setError(null);

            const landlordId = getLandlordId();
            if (!landlordId) {
                setLoading(false);
                return;
            }

            const response = await fetch(ApiDetails + "pegasus/visionary/tickets/api/tickets/analytics", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "token": JSON.parse(originalText)[15][0] !== 'access_token' ? JSON.parse(originalText)[28][1] : JSON.parse(originalText)[15][1]
                },
                body: JSON.stringify({
                    landlord_id: landlordId
                })
            });

             // Handle authentication/authorization errors
                if (response.status === 401) {
                    handle401Unauthorized();
                    return;
                }
                if (response.status === 403) {
                    handle403Forbidden();
                    return;
                }
            if (response.status === 200) {
                const analyticsData = await response.json();

                setTicketTrend(analyticsData.ticketTrend || []);
                setResolutionTime(analyticsData.resolutionTime || []);
                setCategoryDistribution(analyticsData.categoryDistribution || []);
                setPriorityDistribution(analyticsData.priorityDistribution || []);
                setPropertyComparison(analyticsData.propertyComparison || []);
            } else if (response.status === 404) {
                // No data found
                setTicketTrend([]);
                setResolutionTime([]);
                setCategoryDistribution([]);
                setPriorityDistribution([]);
                setPropertyComparison([]);
            } else {
                throw new Error(`Server error: ${response.status}`);
            }
        } catch (error) {
            console.error("Error fetching analytics data:", error);
            setError("Failed to load analytics data. Please try again.");
            showAlert?.("Failed to load analytics data", "error");
        } finally {
            setLoading(false);
        }
    };

    // Effect for handling data source
    useEffect(() => {
        if (!tickets || tickets.length === 0) {
            fetchAnalyticsData();
        } else {
            processTicketData(tickets);
        }
    }, [tickets]);

    // Handle tab change
    const handleTabChange = (event, newValue) => {
        setTabValue(newValue);
    };

    // Chart colors
    const COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#6366F1', '#EC4899', '#8B5CF6', '#14B8A6', '#F97316', '#64748B'];
    const PRIORITY_COLORS = {
        High: '#EF4444',
        HIGH: '#EF4444',
        Medium: '#F59E0B',
        MEDIUM: '#F59E0B',
        Low: '#10B981',
        LOW: '#10B981',
    };

    // Show loading state
    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '300px' }}>
                <CircularProgress />
            </Box>
        );
    }

    // Show error state
    if (error) {
        return (
            <Box sx={{ p: 3 }}>
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
                <Button
                    variant="outlined"
                    onClick={() => tickets ? processTicketData(tickets) : fetchAnalyticsData()}
                >
                    Try Again
                </Button>
            </Box>
        );
    }

    // Check if we have any data to show
    const hasData = ticketTrend.length > 0 ||
        resolutionTime.length > 0 ||
        categoryDistribution.length > 0 ||
        priorityDistribution.length > 0 ||
        propertyComparison.length > 0;

    if (!hasData) {
        return (
            <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography variant="h6" sx={{ mb: 2 }}>No analytics data available</Typography>
                <Typography variant="body1" color="textSecondary" sx={{ mb: 3 }}>
                    There are not enough tickets to generate meaningful analytics. Create more tickets to see trends and insights.
                </Typography>
            </Box>
        );
    }

    return (
        <Box>
            {/* Header section */}
            <Box sx={{ mb: 3 }}>
                <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
                    Maintenance Analytics
                </Typography>
                <Typography variant="body1" color="textSecondary">
                    Analyze trends and patterns in maintenance tickets
                </Typography>
            </Box>

            {/* Analytics tabs */}
            <Paper
                elevation={0}
                sx={{
                    borderRadius: '10px',
                    border: '1px solid rgba(0, 0, 0, 0.08)',
                    overflow: 'hidden',
                    mb: 3
                }}
            >
                <Tabs
                    value={tabValue}
                    onChange={handleTabChange}
                    variant="fullWidth"
                    sx={{
                        borderBottom: 1,
                        borderColor: 'divider',
                        bgcolor: '#F9FAFB'
                    }}
                >
                    <Tab
                        label="Ticket Trends"
                        icon={<TrendingUp size={16} />}
                        iconPosition="start"
                    />
                    <Tab
                        label="Category Analysis"
                        icon={<PieChartIcon size={16} />}
                        iconPosition="start"
                    />
                    <Tab
                        label="Property Comparison"
                        icon={<Home size={16} />}
                        iconPosition="start"
                    />
                    <Tab
                        label="Resolution Time"
                        icon={<BarChart2 size={16} />}
                        iconPosition="start"
                    />
                </Tabs>

                {/* Ticket Trends */}
                <TabPanel value={tabValue} index={0}>
                    <Grid container spacing={3}>
                        <Grid item xs={12}>
                            <Typography variant="h6" sx={{ mb: 2 }}>Ticket Volume by Month</Typography>
                            <Paper
                                elevation={0}
                                sx={{
                                    p: 2,
                                    borderRadius: '10px',
                                    border: '1px solid rgba(0, 0, 0, 0.08)',
                                    height: 400
                                }}
                            >
                                {ticketTrend.length > 0 ? (
                                    <ResponsiveContainer width="100%" height="100%">
                                        <LineChart
                                            data={ticketTrend}
                                            margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
                                        >
                                            <CartesianGrid strokeDasharray="3 3" />
                                            <XAxis dataKey="name" />
                                            <YAxis />
                                            <Tooltip />
                                            <Legend />
                                            <Line
                                                type="monotone"
                                                dataKey="tickets"
                                                name="New Tickets"
                                                stroke="#3B82F6"
                                                strokeWidth={2}
                                                activeDot={{ r: 8 }}
                                            />
                                            <Line
                                                type="monotone"
                                                dataKey="resolved"
                                                name="Resolved Tickets"
                                                stroke="#10B981"
                                                strokeWidth={2}
                                            />
                                        </LineChart>
                                    </ResponsiveContainer>
                                ) : (
                                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                                        <Typography variant="body1" color="textSecondary">No trend data available</Typography>
                                    </Box>
                                )}
                            </Paper>
                        </Grid>
                    </Grid>
                </TabPanel>

                {/* Category Analysis */}
                <TabPanel value={tabValue} index={1}>
                    <Grid container spacing={3}>
                        <Grid item xs={12} md={8}>
                            <Typography variant="h6" sx={{ mb: 2 }}>Tickets by Category</Typography>
                            <Paper
                                elevation={0}
                                sx={{
                                    p: 2,
                                    borderRadius: '10px',
                                    border: '1px solid rgba(0, 0, 0, 0.08)',
                                    height: 400
                                }}
                            >
                                {categoryDistribution.length > 0 ? (
                                    <ResponsiveContainer width="100%" height="100%">
                                        <BarChart
                                            data={categoryDistribution}
                                            layout="vertical"
                                            margin={{ top: 10, right: 30, left: 100, bottom: 0 }}
                                        >
                                            <CartesianGrid strokeDasharray="3 3" />
                                            <XAxis type="number" />
                                            <YAxis dataKey="name" type="category" />
                                            <Tooltip />
                                            <Legend />
                                            <Bar dataKey="value" name="Number of Tickets" fill="#3B82F6" />
                                        </BarChart>
                                    </ResponsiveContainer>
                                ) : (
                                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                                        <Typography variant="body1" color="textSecondary">No category data available</Typography>
                                    </Box>
                                )}
                            </Paper>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <Typography variant="h6" sx={{ mb: 2 }}>Priority Distribution</Typography>
                            <Paper
                                elevation={0}
                                sx={{
                                    p: 2,
                                    borderRadius: '10px',
                                    border: '1px solid rgba(0, 0, 0, 0.08)',
                                    height: 400,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    justifyContent: 'center'
                                }}
                            >
                                {priorityDistribution.some(item => item.value > 0) ? (
                                    <ResponsiveContainer width="100%" height="80%">
                                        <PieChart>
                                            <Pie
                                                data={priorityDistribution}
                                                cx="50%"
                                                cy="50%"
                                                labelLine={false}
                                                outerRadius={80}
                                                fill="#8884d8"
                                                dataKey="value"
                                                label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                                            >
                                                {priorityDistribution.map((entry, index) => (
                                                    <Cell key={`cell-${index}`} fill={PRIORITY_COLORS[entry.name] || COLORS[index % COLORS.length]} />
                                                ))}
                                            </Pie>
                                            <Tooltip />
                                            <Legend />
                                        </PieChart>
                                    </ResponsiveContainer>
                                ) : (
                                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                                        <Typography variant="body1" color="textSecondary">No priority data available</Typography>
                                    </Box>
                                )}
                            </Paper>
                        </Grid>
                    </Grid>
                </TabPanel>

                {/* Property Comparison */}
                <TabPanel value={tabValue} index={2}>
                    <Typography variant="h6" sx={{ mb: 2 }}>Ticket Distribution by Property</Typography>
                    <Paper
                        elevation={0}
                        sx={{
                            p: 2,
                            borderRadius: '10px',
                            border: '1px solid rgba(0, 0, 0, 0.08)',
                            height: 400
                        }}
                    >
                        {propertyComparison.length > 0 ? (
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart
                                    data={propertyComparison}
                                    margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                                >
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis dataKey="property" />
                                    <YAxis />
                                    <Tooltip />
                                    <Legend />
                                    <Bar dataKey="tickets" name="Total Tickets" fill="#3B82F6" />
                                    <Bar dataKey="open" name="Open Tickets" fill="#F59E0B" />
                                    <Bar dataKey="resolved" name="Resolved Tickets" fill="#10B981" />
                                </BarChart>
                            </ResponsiveContainer>
                        ) : (
                            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                                <Typography variant="body1" color="textSecondary">No property data available</Typography>
                            </Box>
                        )}
                    </Paper>
                </TabPanel>

                {/* Resolution Time */}
                <TabPanel value={tabValue} index={3}>
                    <Grid container spacing={3}>
                        <Grid item xs={12}>
                            <Typography variant="h6" sx={{ mb: 2 }}>Average Resolution Time by Month</Typography>
                            <Paper
                                elevation={0}
                                sx={{
                                    p: 2,
                                    borderRadius: '10px',
                                    border: '1px solid rgba(0, 0, 0, 0.08)',
                                    height: 400
                                }}
                            >
                                {resolutionTime.length > 0 ? (
                                    <ResponsiveContainer width="100%" height="100%">
                                        <LineChart
                                            data={resolutionTime}
                                            margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
                                        >
                                            <CartesianGrid strokeDasharray="3 3" />
                                            <XAxis dataKey="name" />
                                            <YAxis />
                                            <Tooltip />
                                            <Legend />
                                            <Line
                                                type="monotone"
                                                dataKey="days"
                                                name="Average Days to Resolve"
                                                stroke="#6366F1"
                                                strokeWidth={2}
                                                activeDot={{ r: 8 }}
                                            />
                                        </LineChart>
                                    </ResponsiveContainer>
                                ) : (
                                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                                        <Typography variant="body1" color="textSecondary">No resolution time data available</Typography>
                                    </Box>
                                )}
                            </Paper>
                        </Grid>
                    </Grid>
                </TabPanel>
            </Paper>
        </Box>
    );
};

export default TicketAnalytics;